import { isHomeSector } from "./faction-routes.js";
import { FACTION_SECTOR_IDS } from "./world-layout.js";
import { cargoOriginSector } from "./resource-origin.js";
import type { CargoItem } from "./risk.js";
import { ALIEN_ENCOUNTERS, ALIEN_CONTRACT_TEMPLATE_IDS } from "./alien-encounters.js";
import { alienContractTerms } from "./alien-contracts.js";
export { contractCountsKill, recordContractKill } from "./contract-kills.js";
import {
  TERRITORY_CONTRACT_TEMPLATE_IDS,
  TERRITORY_CONTRACT_TEMPLATES,
  territoryContractTerms,
} from "./territory-contracts.js";
export { createLegacyContractOffers } from "./legacy-contract-offers.js";
import {
  contractDefinition,
  createRunContract,
  type ContractDefinition,
  type ContractId,
  type ContractResult,
  type NpcArchetype,
  type RunContractState,
  type SelectableContractId,
  type SectorId,
} from "./contracts.js";
import {
  WRECKS_SURVEY_SITE_IDS,
  type ContractDifficulty,
  type SurveySiteId,
} from "./progression.js";
import type { TutorialState } from "./tutorial.js";
import {
  LEGACY_ESCORT_ROUTE_IDS,
  TERRITORY_ESCORT_ROUTE_IDS,
  ESCORT_ROUTES,
  createEscortTerms,
  type EscortRouteId,
  type EscortTerms,
} from "./escort-contracts.js";

import {
  createContractRewardTerms,
  createContractAccessTerms,
  type ContractRewardTerms,
  type ContractAccessTerms,
} from "./reward-rules.js";

export const LEGACY_CONTRACT_CONTENT_VERSION = "contract-orders-v1";
export const REWARDED_CONTRACT_CONTENT_VERSION = "contract-orders-v2";
export const TERRITORIAL_CONTRACT_CONTENT_VERSION = "contract-orders-v3";
export const CONTRACT_CONTENT_VERSION = "contract-orders-v4";
export const CONTRACT_TEMPLATE_IDS = [
  "intro",
  "patrol-hunt",
  "wrecks-specialists",
  "wrecks-salvage",
  "wrecks-survey",
  "frontier-energy",
  "frontier-rescue",
  "escort-meridian-lane",
  "escort-patrol-bypass",
  "escort-wrecks-relief",
  "escort-azure-supply",
  "escort-forge-convoy",
  "escort-magpie-trail",
  ...TERRITORY_CONTRACT_TEMPLATE_IDS,
  ...ALIEN_CONTRACT_TEMPLATE_IDS,
] as const;
export type ContractTemplateId = (typeof CONTRACT_TEMPLATE_IDS)[number];
export interface ContractTermsBase extends ContractDefinition {
  cargoOriginSectorId?: SectorId;
  templateId: ContractTemplateId;
  requiredArchetypes: NpcArchetype[];
  requiredSurveySites: SurveySiteId[];
  requireRescue: boolean;
  requireElite: boolean;
  cargoGoal: number;
  escort: EscortTerms | null;
}
export interface LegacyContractTerms extends ContractTermsBase {
  contentVersion: typeof LEGACY_CONTRACT_CONTENT_VERSION;
  cargoOriginSectorId?: never;
  rewards?: never;
  access?: never;
}
export interface RewardedContractTerms extends ContractTermsBase {
  contentVersion: typeof REWARDED_CONTRACT_CONTENT_VERSION;
  cargoOriginSectorId?: never;
  rewards: ContractRewardTerms;
  access: ContractAccessTerms | null;
}
export interface TerritorialContractTerms extends ContractTermsBase {
  contentVersion: typeof TERRITORIAL_CONTRACT_CONTENT_VERSION;
  rewards: ContractRewardTerms;
  access: ContractAccessTerms | null;
}
export interface CurrentContractTerms extends ContractTermsBase {
  contentVersion: typeof CONTRACT_CONTENT_VERSION;
  killPolicy: "any-hostile" | "required-archetypes";
  rewards: ContractRewardTerms;
  access: ContractAccessTerms | null;
}
export type ContractTerms =
  LegacyContractTerms | RewardedContractTerms | TerritorialContractTerms | CurrentContractTerms;
export interface ContractOffer {
  orderId: string;
  contractId: SelectableContractId;
  templateId: ContractTemplateId;
  standard: ContractTerms;
  advanced: ContractTerms;
}
export type LegacyRunContractState = Omit<
  RunContractState,
  "orderId" | "terms" | "killedArchetypes" | "escort"
>;
export type LegacyContractResult = Omit<ContractResult, "orderId" | "terms">;

export function originalContractTemplate(
  contractId: ContractId,
): ContractTemplateId {
  switch (contractId) {
    case "intro":
      return "intro";
    case "hunt":
      return "patrol-hunt";
    case "salvage":
      return "wrecks-salvage";
    case "signal":
      return "frontier-energy";
    case "escort":
      return "escort-meridian-lane";
  }
}
export function templateContractId(templateId: ContractTemplateId): ContractId {
  if (ALIEN_ENCOUNTERS.some((entry) => entry.templateId === templateId)) return "hunt";
  const territorial = TERRITORY_CONTRACT_TEMPLATES.find(
    (entry) => entry.id === templateId,
  );
  if (territorial) return territorial.family;
  switch (templateId) {
    case "intro":
      return "intro";
    case "patrol-hunt":
    case "wrecks-specialists":
      return "hunt";
    case "wrecks-salvage":
    case "wrecks-survey":
      return "salvage";
    case "frontier-energy":
    case "frontier-rescue":
      return "signal";
    case "escort-meridian-lane":
    case "escort-patrol-bypass":
    case "escort-wrecks-relief":
    case "escort-azure-supply":
    case "escort-forge-convoy":
    case "escort-magpie-trail":
      return "escort";
    default:
      throw new Error(`Unknown contract template ${templateId}`);
  }
}
function baseContractTermsForTemplate(
  templateId: ContractTemplateId,
  difficulty: ContractDifficulty,
): ContractTermsBase {
  const contractId = templateContractId(templateId);
  const definition = contractDefinition(contractId, difficulty);
  const advanced = difficulty === "advanced" && contractId !== "intro";
  const terms: ContractTermsBase = {
    ...definition,
    objective: { ...definition.objective },
    templateId,
    requiredArchetypes: [],
    requiredSurveySites:
      advanced && contractId === "salvage" ? [...WRECKS_SURVEY_SITE_IDS] : [],
    requireRescue: advanced && contractId === "signal",
    requireElite: advanced && contractId === "hunt",
    cargoGoal: definition.cargoKind === null ? 0 : definition.goal,
    escort: null,
  };
  const territorial = TERRITORY_CONTRACT_TEMPLATES.find(
    (entry) => entry.id === templateId,
  );
  if (territorial)
    return territoryContractTerms(territorial, difficulty, terms);
  const alien = alienContractTerms(templateId, difficulty, terms);
  if (alien) return alien;
  if (templateId === "wrecks-specialists") {
    terms.title = advanced
      ? "Ликвидировать штаб заградителей"
      : "Снайпер и заградитель";
    terms.description = advanced
      ? "В поле обломков: четыре боевых зачёта, снайпер, минный заградитель и командир."
      : "В поле обломков уничтожь одного снайпера и одного минного заградителя. Два любых корабля не подходят.";
    terms.sectorId = "wrecks";
    terms.goal = advanced ? 4 : 2;
    terms.credits = advanced ? "270" : "150";
    terms.components = advanced ? 6 : 3;
    terms.requiredArchetypes = ["sniper", "minelayer"];
    terms.objective = {
      name: "Уничтожь снайпера и минного заградителя",
      x: 5320,
      y: 670,
      kind: "combat",
    };
  } else if (templateId === "wrecks-survey") {
    terms.title = advanced
      ? "Полная разведка сектора"
      : "Два сигнала в обломках";
    terms.description = advanced
      ? "Обследуй три скан-маяка и привези два контейнера оборудования."
      : "Физически обследуй северный и южный скан-маяки, затем привези один контейнер оборудования.";
    terms.goal = terms.cargoGoal = advanced ? 2 : 1;
    terms.requiredSurveySites = advanced
      ? [...WRECKS_SURVEY_SITE_IDS]
      : ["wrecks-north", "wrecks-south"];
    terms.credits = advanced ? "190" : "100";
    terms.components = advanced ? 5 : 3;
    terms.objective = {
      name: "Обследуй скан-маяки",
      x: 4400,
      y: 450,
      kind: "survey",
    };
  } else if (templateId === "frontier-rescue") {
    terms.title = advanced
      ? "Спасти «Вегу» и доставить энергию"
      : "Принять сигнал «Веги»";
    terms.description = advanced
      ? "Лично помоги транспорту уроном пиратам или реальным ремонтом и доставь одну энергию."
      : "Спаси транспорт: нанеси пиратам настоящий урон или почини повреждённый транспорт. Груз не требуется.";
    terms.goal = 1;
    terms.cargoGoal = advanced ? 1 : 0;
    terms.cargoKind = advanced ? "energy" : null;
    terms.requireRescue = true;
    terms.credits = advanced ? "190" : "150";
    terms.components = advanced ? 5 : 3;
    terms.objective = {
      name: "Помоги спасти транспорт «Вега»",
      x: 8810,
      y: 780,
      kind: "travel",
    };
  } else if (contractId === "escort") {
    const routeId = escortTemplateRoute(templateId);
    const route = ESCORT_ROUTES[routeId];
    terms.escort = createEscortTerms(routeId, difficulty);
    terms.title = `${route.title}${advanced ? " / Ответственное сопровождение" : ""}`;
    terms.description = `${route.description} Пройди рядом не менее ${advanced ? "80" : "55"}% пути${advanced ? " и внеси 25 урона или реального ремонта" : ""}. Награда после прибытия транспорта и возвращения на станцию.`;
    terms.sectorId = route.sectorId;
    terms.credits =
      routeId === "wrecks-relief"
        ? advanced
          ? "320"
          : "200"
        : advanced
          ? "250"
          : "150";
    terms.components =
      routeId === "wrecks-relief" ? (advanced ? 7 : 4) : advanced ? 5 : 3;
    const start = terms.escort.path[0];
    if (!start) throw new Error("An escort route must have a start");
    terms.objective = {
      name: `Встреть транспорт: ${route.title}`,
      ...start,
      kind: "travel",
    };
  }
  return terms;
}

export function contractTermsForTemplate(
  templateId: ContractTemplateId,
  difficulty: ContractDifficulty,
): CurrentContractTerms {
  const terms = baseContractTermsForTemplate(templateId, difficulty);
  return {
    ...terms,
    contentVersion: CONTRACT_CONTENT_VERSION,
    killPolicy: ALIEN_ENCOUNTERS.some((entry) => entry.templateId === templateId)
      ? "required-archetypes" : "any-hostile",
    rewards: createContractRewardTerms(terms.id, difficulty),
    access: createContractAccessTerms(terms.id, difficulty),
  };
}
function legacyBaseTerms(
  templateId: ContractTemplateId,
  difficulty: ContractDifficulty,
) {
  const { cargoOriginSectorId: _origin, ...terms } =
    baseContractTermsForTemplate(templateId, difficulty);
  return terms;
}
function legacyContractTermsForTemplate(
  templateId: ContractTemplateId,
  difficulty: ContractDifficulty,
): LegacyContractTerms {
  return {
    ...legacyBaseTerms(templateId, difficulty),
    contentVersion: LEGACY_CONTRACT_CONTENT_VERSION,
  };
}

function escortTemplateRoute(templateId: ContractTemplateId): EscortRouteId {
  switch (templateId) {
    case "escort-meridian-lane":
      return "meridian-lane";
    case "escort-patrol-bypass":
      return "patrol-bypass";
    case "escort-wrecks-relief":
      return "wrecks-relief";
    case "escort-azure-supply":
      return "azure-supply";
    case "escort-forge-convoy":
      return "forge-convoy";
    case "escort-magpie-trail":
      return "magpie-trail";
    default:
      throw new Error("The template is not an escort route");
  }
}

export function cloneContractOffer(offer: ContractOffer): ContractOffer {
  return {
    ...offer,
    standard: cloneContractTerms(offer.standard),
    advanced: cloneContractTerms(offer.advanced),
  };
}

export function cloneContractTerms(terms: ContractTerms): ContractTerms {
  return structuredClone(terms);
}
export function createContractOffers(
  playerId: string,
  revision: number,
): ContractOffer[] {
  if (!playerId || !Number.isSafeInteger(revision) || revision < 0)
    throw new Error("Contract board identity and revision must be valid");
  const families = ["hunt", "salvage", "signal"] as const;
  const offers = families.map((family, index): ContractOffer => {
    const start = ((revision % FACTION_SECTOR_IDS.length) + index * 5) % FACTION_SECTOR_IDS.length;
    const location = [...FACTION_SECTOR_IDS.slice(start), ...FACTION_SECTOR_IDS.slice(0, start)]
      .find((id) => !isHomeSector(id));
    const template = TERRITORY_CONTRACT_TEMPLATES.find(
      (entry) => entry.family === family && entry.location.id === location,
    );
    if (!template) throw new Error("Missing territorial contract template");
    const alien = family === "hunt" && Math.floor(revision / FACTION_SECTOR_IDS.length) % 2 === 0
      ? ALIEN_ENCOUNTERS.find((entry) => entry.sectorId === location) : undefined;
    const templateId = alien?.templateId ?? template.id;
    return {
      orderId: `${playerId}:board:${revision}:${family}:${templateId}`,
      contractId: family,
      templateId,
      standard: contractTermsForTemplate(templateId, "standard"),
      advanced: contractTermsForTemplate(templateId, "advanced"),
    };
  });
  const routes =
    revision % 2 === 0 ? TERRITORY_ESCORT_ROUTE_IDS : LEGACY_ESCORT_ROUTE_IDS;
  return [
    ...offers,
    ...routes.map((routeId): ContractOffer => {
      const templateId: ContractTemplateId = `escort-${routeId}`;
      return {
        orderId: `${playerId}:board:${revision}:escort:${routeId}`,
        contractId: "escort",
        templateId,
        standard: contractTermsForTemplate(templateId, "standard"),
        advanced: contractTermsForTemplate(templateId, "advanced"),
      };
    }),
  ];
}
export function createRunContractFromOffer(
  runId: string,
  offer: ContractOffer,
  difficulty: ContractDifficulty,
): RunContractState {
  const terms = cloneContractTerms(offer[difficulty]);
  return {
    ...createRunContract(runId, offer.contractId, difficulty),
    orderId: offer.orderId,
    terms,
    sectorId: terms.sectorId,
    goal: terms.goal,
  };
}
export function decorateLegacyRunContract(
  contract: LegacyRunContractState,
): RunContractState {
  const terms = legacyContractTermsForTemplate(
    originalContractTemplate(contract.contractId),
    contract.difficulty,
  );
  terms.goal = contract.goal;
  terms.sectorId = contract.sectorId;
  terms.cargoGoal = terms.cargoKind === null ? 0 : contract.goal;
  return {
    ...contract,
    orderId: `${contract.runId}:legacy:${contract.contractId}`,
    terms,
    killedArchetypes: [],
    surveyedSites: [...contract.surveyedSites],
    escort: null,
  };
}
export function decorateLegacyContractResult(
  result: LegacyContractResult,
  frozenTerms?: ContractTerms,
): ContractResult {
  return {
    ...result,
    orderId: `${result.runId}:legacy:${result.contractId}`,
    terms: cloneContractTerms(
      frozenTerms ??
        legacyContractTermsForTemplate(
          originalContractTemplate(result.contractId),
          result.difficulty,
        ),
    ),
  };
}
export function cloneRunContract(contract: RunContractState): RunContractState {
  return {
    ...contract,
    terms: cloneContractTerms(contract.terms),
    killedArchetypes: [...contract.killedArchetypes],
    surveyedSites: [...contract.surveyedSites],
    escort: contract.escort ? { ...contract.escort } : null,
  };
}
export function isActiveContract(
  contract: Pick<RunContractState, "stage" | "outcome">,
): boolean {
  return contract.stage !== "settled" && contract.outcome === "active";
}
export function hasActiveIntro(
  contracts: readonly RunContractState[],
): boolean {
  return contracts.some(
    (contract) => contract.contractId === "intro" && isActiveContract(contract),
  );
}
export function hasIncompleteTutorial(pilot: {
  contracts: readonly RunContractState[];
  tutorial: TutorialState | null;
}): boolean {
  return pilot.tutorial
    ? !pilot.tutorial.docked
    : hasActiveIntro(pilot.contracts);
}

export function contractCargoMatches(
  terms: ContractTerms,
  item: Pick<CargoItem, "kind" | "sourceId">,
): boolean {
  return (
    item.kind === terms.cargoKind &&
    (terms.cargoOriginSectorId === undefined ||
      cargoOriginSector(item.sourceId) === terms.cargoOriginSectorId)
  );
}
